package com.arena_web.pages.events

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.EventDetailUiState
import com.arena_web.core.viewmodel.EventDetailViewModel
import com.arena_web.data.dto.UpdateEventRequest
import com.arena_web.core.viewmodel.UserParticipation
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.di.LocalAppContainer
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.components.widgets.LoadingSpinner
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.components.events.CreateEventPopup
import com.arena_web.ui.components.events.CreateTeamPopup
import com.arena_web.ui.components.events.DeleteEventConfirmDialog
import com.arena_web.ui.components.events.InviteTeamPopup
import com.arena_web.ui.components.events.MatchActionConfirmDialog
import com.arena_web.ui.components.events.StartEventConfirmDialog
import com.arena_web.ui.sections.eventdetail.CompletedMatchBanner
import com.arena_web.ui.sections.eventdetail.EventChatPanel
import com.arena_web.ui.sections.eventdetail.FormatOverridePill
import com.arena_web.ui.sections.eventdetail.LineupsFieldDisplay
import com.arena_web.ui.sections.eventdetail.NonProCompletedLineups
import com.arena_web.ui.sections.eventdetail.MatchCenterTab
import com.arena_web.ui.sections.eventdetail.MatchCenterTabs
import com.arena_web.ui.sections.eventdetail.MatchEndedHeader
import com.arena_web.ui.sections.eventdetail.MatchInfoBanner
import com.arena_web.ui.sections.eventdetail.MatchMedia
import com.arena_web.ui.sections.eventdetail.MatchPerformance
import com.arena_web.ui.sections.eventdetail.MatchView
import com.arena_web.ui.sections.eventdetail.PerformanceLeaders
import com.arena_web.ui.sections.eventdetail.PlayersGrid
import com.arena_web.ui.sections.eventdetail.TeamColorPills
import com.arena_web.ui.sections.eventdetail.mobile.EventDetailContentMobile
import com.arena_web.ui.sections.eventdetail.mobile.MatchViewMobile
import com.arena_web.ui.sections.profile.mobile.openUserProfile
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import org.w3c.files.get

@Page("/events/{id}")
@Composable
fun EventDetailPage() {
    val t = LocalAppStrings.current
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val eventId = ctx.route.path.substringAfterLast("/")
    val isLoggedIn = Storage.get(StorageKeys.ACCESS_TOKEN) != null

    val viewModel = rememberViewModel(key = eventId) {
        EventDetailViewModel(
            eventId = eventId,
            eventRepository = container.eventRepository,
            publicEventRepository = container.publicEventRepository,
            teamRepository = container.teamRepository,
            chatRepository = container.chatRepository,
        )
    }

    LaunchedEffect(viewModel) {
        viewModel.onEventDeletedRemotely {
            ctx.router.navigateTo(Routes.ARENA)
        }
    }

    val state by viewModel.uiState.collectAsState()

    // Header's "My Activity" dropdown caches its events list and only
    // refetches on first open. When the event's status changes out of
    // the active set (NEW / IN_PROGRESS) the cached entry is stale,
    // so invalidate the cache to drop the now-completed/cancelled event
    // from the header.
    LaunchedEffect(state.event?.status) {
        val status = state.event?.status?.uppercase()
        if (status == "COMPLETED" || status == "CANCELLED") {
            container.refreshActivity()
        }
    }
    var showCreateTeamPopup by remember { mutableStateOf(false) }
    var showInviteTeamPopup by remember { mutableStateOf(false) }
    var showEditEventPopup by remember { mutableStateOf(false) }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var showStartConfirm by remember { mutableStateOf(false) }
    var showEndMatchConfirm by remember { mutableStateOf(false) }
    var showResetTimerConfirm by remember { mutableStateOf(false) }
    var showAuthGate by remember { mutableStateOf(false) }
    var showAddMediaPopup by remember { mutableStateOf(false) }
    var activeMatchTab by remember { mutableStateOf(MatchCenterTab.MatchInfo) }

    // Event media uploads are a subscription feature (mobile parity): the
    // Add Media affordances carry a PRO+ pill and open the paywall instead
    // of the upload sheet while unsubscribed.
    val isSubscribed by container.isSubscribed.collectAsState()
    var mediaPaywallOpen by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) { container.ensureSubscriptionLoaded() }
    val requestAddMedia: () -> Unit = {
        if (isSubscribed) showAddMediaPopup = true else mediaPaywallOpen = true
    }

    // Optimistic format override for the COMPLETED Lineups tab (admin-only).
    // Mirrors MatchView's pattern: set immediately for instant UI feedback,
    // PUT the new format via editEvent, then clear once the server-side
    // value catches up via EVENT_UPDATED so we don't double-source.
    var lineupsFormatOverride by remember { mutableStateOf<Int?>(null) }
    LaunchedEffect(state.event?.format) {
        if (lineupsFormatOverride == state.event?.format) lineupsFormatOverride = null
    }

    val isLive = state.event?.status?.uppercase() == "IN_PROGRESS"
    val isCompleted = state.event?.status?.uppercase() == "COMPLETED"
    val showsPitchBackground = isLive || (isCompleted && activeMatchTab == MatchCenterTab.Lineups)
    // Background picks the sport-specific pitch art when the page is hosting
    // either the live match view or a completed Lineups tab — both lean on
    // the stadium image as the primary surface. Falls back to football when
    // the sport is unknown / "other".
    val liveBackground = if (showsPitchBackground)
        AppIcons.eventFieldBackground(state.event?.sportName ?: "football")
    else null
    val mobile = isMobile()
    AppPageLayout(
        activeTab = SidebarTab.Arena,
        // The mobile live view and the mobile completed Lineups tab draw
        // their own ambient pitch image — the page stays on the default
        // dark background on phones.
        backgroundImage = if (mobile) null else liveBackground,
        mobileReady = true,
        mobileHideBottomNav = true,
        mobileHideTopBar = true,
        mobileEdgeToEdge = mobile,
    ) {
      if (mobile && isLive) {
        MatchViewMobile(
            state = state,
            onBack = { ctx.router.navigateTo(Routes.ARENA) },
            onEndMatch = { showEndMatchConfirm = true },
            onScoreUp = { teamId -> viewModel.updateScore(teamId, 1) },
            onScoreDown = { teamId -> viewModel.updateScore(teamId, -1) },
            onToggleTimer = { viewModel.toggleTimer() },
            onResetTimer = { showResetTimerConfirm = true },
            onTimerTick = { viewModel.tickTimer() },
            onViewProfile = { userId -> openUserProfile(ctx, userId) },
            onSetPlayerStat = { userId, statTypeId, value ->
                viewModel.setPlayerStat(userId, statTypeId, value)
            },
            onSwapLineup = { userIdA, userIdB -> viewModel.swapLineup(userIdA, userIdB) },
            // Same persistence path as desktop MatchView's format pill —
            // the backend broadcasts EVENT_UPDATED and every viewer resyncs.
            onUpdateFormat = { newFormat ->
                viewModel.editEvent(UpdateEventRequest(format = newFormat))
            },
        )
      } else if (mobile) {
        EventDetailContentMobile(
            state = state,
            viewModel = viewModel,
            isLoggedIn = isLoggedIn,
            onBack = { ctx.router.navigateTo(Routes.ARENA) },
            onJoin = { if (isLoggedIn) viewModel.joinEvent() else showAuthGate = true },
            onLeave = { viewModel.leaveEvent() },
            onStart = { showStartConfirm = true },
            onEdit = { showEditEventPopup = true },
            onDelete = { showDeleteConfirm = true },
            onCreateTeam = { showCreateTeamPopup = true },
            onAddPlayer = {
                if (!isLoggedIn) {
                    showAuthGate = true
                } else {
                    val uid = Storage.get(StorageKeys.USER_ID)
                    val participant = uid?.let { id -> state.event?.participants?.find { it.userId == id } }
                    when {
                        participant?.eventTeamId != null -> viewModel.leaveTeam()
                        participant == null -> viewModel.joinEvent()
                        else -> {}
                    }
                }
            },
            onConfirmPlayer = { viewModel.confirmParticipant(it) },
            onRejectPlayer = { viewModel.rejectParticipant(it) },
            onRemoveFromEvent = { userId -> viewModel.rejectParticipant(userId) },
            onToggleAdmin = { userId, makeAdmin -> viewModel.toggleParticipantAdmin(userId, makeAdmin) },
            onAssignToTeam = { userId, eventTeamId -> viewModel.assignPlayerToTeam(userId, eventTeamId) },
            // Phones open the other-user profile as a bottom sheet (not a route).
            onViewProfile = { userId -> openUserProfile(ctx, userId) },
            onDeleteTeam = { teamId -> viewModel.deleteTeam(teamId) },
            onChatAttachmentClick = {
                pickEventChatFile { bytes, name, contentType ->
                    viewModel.setChatPendingFile(bytes, name, contentType)
                }
            },
            activeMatchTab = activeMatchTab,
            onMatchTabSelected = { activeMatchTab = it },
            onAddMedia = requestAddMedia,
            mediaLocked = !isSubscribed,
        )
      } else {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(32, 40)
                .sPaddingBottom(30)
                .sGap(24)
        ) {
            // Back button row. For Pro completed events (Match Info / Lineups
            // tabs) the segmented control floats absolutely-centered in this
            // same row so the Back link stays anchored to the left.
            val showFloatingTabs = state.event?.status?.uppercase() == "COMPLETED"
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .position(Position.Relative),
                contentAlignment = Alignment.CenterStart
            ) {
                Row(
                    modifier = Modifier
                        .sGap(8)
                        .cursor(Cursor.Pointer)
                        .onClick { ctx.router.navigateTo(Routes.ARENA) },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        src = AppIcons.ChevronForward,
                        modifier = Modifier.styleModifier {
                            property("transform", "rotate(180deg)")
                        }
                    )
                    SpanText(
                        t.pageBackToEvents,
                        modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
                if (showFloatingTabs) {
                    Box(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .fillMaxWidth(),
                        contentAlignment = Alignment.CenterEnd
                    ) {
                        MatchCenterTabs(
                            activeTab = activeMatchTab,
                            onTabSelected = { activeMatchTab = it }
                        )
                    }
                }
            }

            if (state.isLoading) {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center
                ) {
                    // Intentionally empty — no loading indicator (user pref).
                }
            } else if (state.error != null) {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        state.error ?: t.pageSomethingWentWrong,
                        modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            } else if (state.event?.status?.uppercase() == "IN_PROGRESS") {
                // IN_PROGRESS: Full-screen match view
                MatchView(
                    state = state,
                    onEndMatch = { showEndMatchConfirm = true },
                    onScoreUp = { teamId -> viewModel.updateScore(teamId, 1) },
                    onScoreDown = { teamId -> viewModel.updateScore(teamId, -1) },
                    onToggleTimer = { viewModel.toggleTimer() },
                    onResetTimer = { showResetTimerConfirm = true },
                    onTimerTick = { viewModel.tickTimer() },
                    onToggleExtendedStats = { viewModel.toggleExtendedStats() },
                    onViewProfile = { userId -> ctx.router.navigateTo(Routes.userProfile(userId)) },
                    onSetPlayerStat = { userId, statTypeId, value ->
                        viewModel.setPlayerStat(userId, statTypeId, value)
                    },
                    onSwapLineup = { userIdA, userIdB ->
                        viewModel.swapLineup(userIdA, userIdB)
                    },
                    onUpdateFormat = { newFormat ->
                        // Persist the format change on the backend — `editEvent`
                        // PUTs `UpdateEventRequest(format = …)` and the server
                        // broadcasts `EVENT_UPDATED` so every viewer's UI
                        // converges on the new size.
                        viewModel.editEvent(UpdateEventRequest(format = newFormat))
                    },
                    onSwapEventTeams = { teamIdA, teamIdB ->
                        viewModel.swapEventTeams(teamIdA, teamIdB)
                    },
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .minHeight(0.px)
                )
            } else if (state.event?.status?.uppercase() == "COMPLETED") {
                val isProCompleted = state.event?.isPro == true

                if (isProCompleted) {
                    // Pro completed (Figma 8838:340399 / 8838:342244 / 8838:345915):
                    // segmented Match Info / Lineups / Media control lives in the
                    // Back-to-Events row (right-aligned) and stays put across all
                    // three tabs — only the content below switches.
                    when (activeMatchTab) {
                        MatchCenterTab.Media -> {
                            // "Add Media" button — shown to every CONFIRMED
                            // participant (matches the backend rule in
                            // `UploadEventMediaUseCase`, which checks
                            // participant status, not admin).
                            if (state.userParticipation == UserParticipation.CONFIRMED) {
                                AddMediaButtonRow(onClick = requestAddMedia, showProPlus = !isSubscribed)
                            }

                            MatchMedia(
                                mediaItems = state.mediaItems,
                                isLoading = state.mediaLoading,
                                currentUserId = Storage.get(StorageKeys.USER_ID),
                                isAdmin = state.isAdmin,
                                onDeleteMedia = { viewModel.deleteMedia(it) },
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .flexGrow(1)
                                    .minHeight(0.px)
                            )
                        }
                        MatchCenterTab.Lineups -> {
                            // Lineups (Figma 7902:348058) — read-only twin of
                            // the live MatchView formation field. Same per-
                            // format card geometry, same team crests, same
                            // bench row. Pitch background sits on AppPageLayout.
                            // Admins get a Format dropdown right under the tab
                            // switcher so they can still resize the formation
                            // post-match (same optimistic override + persist
                            // pattern as the live view).
                            if (state.isAdmin) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.End,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    FormatOverridePill(
                                        overrideFormat = lineupsFormatOverride,
                                        onFormatChange = { picked ->
                                            if (picked != null && picked != state.event?.format) {
                                                lineupsFormatOverride = picked
                                                viewModel.editEvent(UpdateEventRequest(format = picked))
                                            } else {
                                                lineupsFormatOverride = null
                                            }
                                        },
                                        sportName = state.event?.sportName ?: "football",
                                        fallbackFormat = state.event?.format,
                                    )
                                }
                            }
                            LineupsFieldDisplay(
                                state = state,
                                onViewProfile = { userId -> ctx.router.navigateTo(Routes.userProfile(userId)) },
                                formatOverride = lineupsFormatOverride,
                                onRatePlayer = { userId, ratings ->
                                    viewModel.ratePlayer(userId, ratings)
                                },
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .flexGrow(1)
                                    .minHeight(0.px)
                            )
                        }
                        else -> {
                            // MatchInfo / Lineups: Two-column layout per Figma —
                            // left ~1297, right ~525 (banner+leaders | perf+chat).
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .flexGrow(1)
                                    .minHeight(0.px)
                                    .sGap(18)
                                    .styleModifier { property("align-items", "stretch") }
                            ) {
                                // Left column: Banner (with tabs overlaid) + Performance Leaders
                                Column(
                                    modifier = Modifier
                                        .fillMaxHeight()
                                        .sGap(20)
                                        .styleModifier { property("flex", "1297 1 0px") }
                                ) {
                                    state.event?.let { event ->
                                        CompletedMatchBanner(event = event)
                                    }

                                    state.event?.let { event ->
                                        PerformanceLeaders(
                                            event = event,
                                            playerStats = state.playerStats,
                                            onViewProfile = { userId -> ctx.router.navigateTo(Routes.userProfile(userId)) },
                                            modifier = Modifier.flexGrow(1).minHeight(0.px)
                                        )
                                    }
                                }

                                // Right column: Match Performance hero (sized to
                                // fit team logo + name + score + header without
                                // clipping the big score numeral) + Chat growing
                                // to fill the rest.
                                Column(
                                    modifier = Modifier
                                        .fillMaxHeight()
                                        .sGap(18)
                                        .styleModifier { property("flex", "525 1 0px") }
                                ) {
                                    state.event?.let { event ->
                                        Box(modifier = Modifier.fillMaxWidth().sHeight(300)) {
                                            MatchPerformance(
                                                event = event,
                                                modifier = Modifier.fillMaxWidth().fillMaxHeight()
                                            )
                                        }
                                    }
                                    EventChatPanelBound(
                                        viewModel = viewModel,
                                        state = state,
                                        isLoggedIn = isLoggedIn,
                                        onNavigateToLogin = { showAuthGate = true },
                                        onViewProfile = { userId -> ctx.router.navigateTo(Routes.userProfile(userId)) },
                                        modifier = Modifier.fillMaxWidth().flexGrow(1).minHeight(0.px)
                                    )
                                }
                            }
                        }
                    }
                } else {
                    // Non-Pro completed — segmented tabs route to:
                    //  • MatchInfo: large banner left + chat right (Figma 7902:347147)
                    //  • Lineups: pitch background + headline + player grid (Figma 7893:88005)
                    //  • Media: shared media grid.
                    when (activeMatchTab) {
                        MatchCenterTab.Media -> {
                            if (state.userParticipation == UserParticipation.CONFIRMED) {
                                AddMediaButtonRow(onClick = requestAddMedia, showProPlus = !isSubscribed)
                            }
                            MatchMedia(
                                mediaItems = state.mediaItems,
                                isLoading = state.mediaLoading,
                                currentUserId = Storage.get(StorageKeys.USER_ID),
                                isAdmin = state.isAdmin,
                                onDeleteMedia = { viewModel.deleteMedia(it) },
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .flexGrow(1)
                                    .minHeight(0.px)
                            )
                        }
                        MatchCenterTab.Lineups -> {
                            state.event?.let { event ->
                                NonProCompletedLineups(
                                    event = event,
                                    onViewProfile = { userId -> ctx.router.navigateTo(Routes.userProfile(userId)) },
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .flexGrow(1)
                                        .minHeight(0.px)
                                )
                            }
                        }
                        else -> {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .flexGrow(1)
                                    .minHeight(0.px)
                                    .sGap(18)
                                    .styleModifier { property("align-items", "stretch") }
                            ) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxHeight()
                                        .styleModifier { property("flex", "1297 1 0px") }
                                ) {
                                    state.event?.let { event ->
                                        CompletedMatchBanner(
                                            event = event,
                                            fillHeight = true,
                                        )
                                    }
                                }
                                EventChatPanelBound(
                                    viewModel = viewModel,
                                    state = state,
                                    isLoggedIn = isLoggedIn,
                                    onNavigateToLogin = { showAuthGate = true },
                                    onViewProfile = { userId -> ctx.router.navigateTo(Routes.userProfile(userId)) },
                                    modifier = Modifier
                                        .fillMaxHeight()
                                        .styleModifier { property("flex", "525 1 0px") }
                                )
                            }
                        }
                    }
                }
            } else {
                val isPro = state.event?.isPro == true

                // NEW / other statuses: two-column layout. Pro events follow Figma
                // node 7069:711099 — banner + team colour legend + players grid in
                // the left column, chat panel in the right column.
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .minHeight(0.px)
                        .sGap(20)
                        .styleModifier {
                            property("align-items", "stretch")
                        }
                ) {
                    // Left column: Banner + (TeamPills + PlayersGrid for isPro, else PlayersGrid)
                    Column(
                        modifier = Modifier
                            .sGap(20)
                            .styleModifier { property("flex", if (isPro) "1297 1 0px" else "1297 1 0px") }
                    ) {
                        MatchInfoBanner(
                            state = state,
                            onJoinClick = {
                                if (isLoggedIn) {
                                    viewModel.joinEvent()
                                } else {
                                    showAuthGate = true
                                }
                            },
                            onLeaveClick = { viewModel.leaveEvent() },
                            onStartClick = { showStartConfirm = true },
                            onEditClick = { showEditEventPopup = true },
                            onDeleteClick = { showDeleteConfirm = true }
                        )

                        if (isPro) {
                            state.event?.let { event ->
                                TeamColorPills(
                                    teams = event.teams,
                                    isAdmin = state.isAdmin,
                                    onCreateTeam = { showCreateTeamPopup = true },
                                )
                            }
                        }

                        state.event?.let { event ->
                            PlayersGrid(
                                event = event,
                                isAdmin = state.isAdmin,
                                onAddPlayerClick = {
                                    if (!isLoggedIn) {
                                        showAuthGate = true
                                        return@PlayersGrid
                                    }
                                    val currentUserId = Storage.get(StorageKeys.ACCESS_TOKEN)?.let {
                                        Storage.get(StorageKeys.USER_ID)
                                    }
                                    val participant = currentUserId?.let { uid ->
                                        event.participants.find { it.userId == uid }
                                    }
                                    when {
                                        participant?.eventTeamId != null -> viewModel.leaveTeam()
                                        participant == null -> viewModel.joinEvent()
                                    }
                                },
                                onConfirmPlayer = { viewModel.confirmParticipant(it) },
                                onRejectPlayer = { viewModel.rejectParticipant(it) },
                                onRemoveFromEvent = { userId -> viewModel.rejectParticipant(userId) },
                                onToggleAdmin = { userId, makeAdmin -> viewModel.toggleParticipantAdmin(userId, makeAdmin) },
                                onAssignToTeam = { userId, eventTeamId -> viewModel.assignPlayerToTeam(userId, eventTeamId) },
                                onViewProfile = { userId -> ctx.router.navigateTo(Routes.userProfile(userId)) },
                                modifier = Modifier.fillMaxWidth().flexGrow(1)
                            )
                        }
                    }

                    // Right column: chat panel — always rendered. Gated states
                    // (not joined / pending approval) and empty-messages state
                    // are handled inside the panel.
                    EventChatPanelBound(
                        viewModel = viewModel,
                        state = state,
                        isLoggedIn = isLoggedIn,
                        onNavigateToLogin = { showAuthGate = true },
                        onViewProfile = { userId -> ctx.router.navigateTo(Routes.userProfile(userId)) },
                        modifier = Modifier.styleModifier {
                            property("flex", if (isPro) "523 1 0px" else "525 1 0px")
                        }
                    )
                }
            }
        }
      }
    }

    CreateTeamPopup(
        isOpen = showCreateTeamPopup,
        onClose = { showCreateTeamPopup = false },
        onCreated = { name, color ->
            viewModel.createEventTeam(name, color)
            showCreateTeamPopup = false
        },
        usedColors = state.event?.teams?.mapNotNull { it.color } ?: emptyList(),
    )

    InviteTeamPopup(
        isOpen = showInviteTeamPopup,
        onClose = {
            showInviteTeamPopup = false
            viewModel.clearTeamSearch()
        },
        searchResults = state.teamSearchResults,
        invitations = state.invitations,
        isSearching = state.teamSearchLoading,
        inviteInProgress = state.inviteInProgress,
        onSearch = { viewModel.searchTeams(it) },
        onInvite = { viewModel.inviteTeam(it) }
    )

    // Edit reuses the create modal with the existing event seeded as the
    // initial form state — same layout, same styling, same controls.
    CreateEventPopup(
        isOpen = showEditEventPopup,
        editingEvent = state.event,
        onClose = { showEditEventPopup = false },
        onCreated = { updated ->
            viewModel.applyEventUpdate(updated)
            showEditEventPopup = false
        }
    )

    DeleteEventConfirmDialog(
        isOpen = showDeleteConfirm,
        eventTitle = state.event?.title ?: "",
        isDeleting = state.actionInProgress,
        participantCount = state.event?.participants?.size ?: 1,
        onCancel = { showDeleteConfirm = false },
        onConfirm = {
            viewModel.deleteEvent(
                onSuccess = {
                    showDeleteConfirm = false
                    ctx.router.navigateTo(Routes.ARENA)
                },
                onError = { /* state.error will display */ }
            )
        }
    )

    // Pre-start warnings: pending join requests only matter on PRIVATE
    // events (PUBLIC auto-confirms); the "no team" warning is Pro-only
    // since regular events don't carry team assignments at all.
    val startEvent = state.event
    val pendingForStart = if (startEvent != null && startEvent.privacy.uppercase() == "PRIVATE") {
        startEvent.participants.filter { it.status.uppercase() == "PENDING" }
    } else emptyList()
    val teamlessForStart = if (startEvent != null && startEvent.isPro) {
        startEvent.participants.filter {
            it.status.uppercase() == "CONFIRMED" && it.eventTeamId == null
        }
    } else emptyList()

    StartEventConfirmDialog(
        isOpen = showStartConfirm,
        eventTitle = startEvent?.title ?: "",
        isStarting = state.actionInProgress,
        sportName = startEvent?.sportName ?: "football",
        pendingParticipants = pendingForStart,
        teamlessParticipants = teamlessForStart,
        onCancel = { showStartConfirm = false },
        onConfirm = {
            viewModel.startEvent()
            showStartConfirm = false
        }
    )

    // On phones the confirm dialogs must render at the mobile baseline —
    // same --scale wrap the mobile event screen uses for its own dialogs.
    Box(
        modifier = if (mobile) {
            Modifier.styleModifier { property("--scale", "var(--mobile-scale)") }
        } else Modifier
    ) {
    MatchActionConfirmDialog(
        isOpen = showResetTimerConfirm,
        title = t.pageResetTimerTitle,
        message = t.pageResetTimerMessage,
        confirmText = t.pageResetTimerConfirm,
        iconSrc = AppIcons.Time,
        iconAccent = "#0061FF",
        isBusy = state.actionInProgress,
        busyText = t.pageResetting,
        onCancel = { showResetTimerConfirm = false },
        onConfirm = {
            viewModel.resetTimer()
            showResetTimerConfirm = false
        }
    )

    // Auto-close the End-match dialog once the backend transitions the
    // event out of IN_PROGRESS (success path: → COMPLETED, also handles
    // CANCELLED). The dialog stays open with its busy spinner during the
    // round-trip so the admin has clear feedback the action is in flight.
    LaunchedEffect(state.event?.status) {
        if (showEndMatchConfirm &&
            state.event?.status?.uppercase() != "IN_PROGRESS"
        ) {
            showEndMatchConfirm = false
        }
    }

    MatchActionConfirmDialog(
        isOpen = showEndMatchConfirm,
        title = t.pageEndMatchTitle,
        message = t.pageEndMatchMessage,
        confirmText = t.pageEndMatchConfirm,
        iconSrc = AppIcons.Trophy,
        iconAccent = "#0061FF",
        isBusy = state.actionInProgress,
        busyText = t.pageEnding,
        onCancel = { if (!state.actionInProgress) showEndMatchConfirm = false },
        onConfirm = {
            // Don't close here — wait for the status change (handled by
            // the `LaunchedEffect` above) so the busy state is visible
            // for the duration of the request.
            viewModel.endMatch()
        }
    )
    }

    com.arena_web.ui.components.overlay.AccountRequiredModal(
        isOpen = showAuthGate,
        onClose = { showAuthGate = false },
        onSignedIn = {
            showAuthGate = false
            kotlinx.browser.window.location.reload()
        }
    )

    // A batch that ends with failures reopens the Add Media sheet with the
    // failure report at the bottom (file preview + duration + reason).
    LaunchedEffect(state.failedMediaUploads) {
        if (state.failedMediaUploads.isNotEmpty()) showAddMediaPopup = true
    }

    com.arena_web.ui.components.events.AddMediaPopup(
        isOpen = showAddMediaPopup,
        onClose = {
            showAddMediaPopup = false
            viewModel.clearFailedMediaUploads()
        },
        // Fire-and-forget: the popup closes instantly and the batch runs in
        // the viewmodel — loader tiles at the top of the media grid, then a
        // failed-uploads report if anything didn't make it. Compression
        // (≤1920px JPEG) happens inside the batch.
        onUpload = { files ->
            viewModel.clearFailedMediaUploads()
            viewModel.uploadMediaBatch(
                files.map { com.arena_web.core.viewmodel.MediaUploadRequest(it.file, it.previewUrl) }
            )
        },
        failedUploads = state.failedMediaUploads,
        onDismissFailed = { viewModel.removeFailedMediaUpload(it) },
    )

    // Media-upload paywall — above the mobile sheet chrome (z 1100).
    com.arena_web.ui.components.overlay.SubscribeModal(
        isOpen = mediaPaywallOpen,
        onClose = { mediaPaywallOpen = false },
        zIndex = 1200,
        source = "event_media",
    )
}

private const val MAX_EVENT_CHAT_FILE_BYTES = 50 * 1024 * 1024 // 50 MB

private fun pickEventChatFile(onFilePicked: (ByteArray, String, String) -> Unit) {
    val input = kotlinx.browser.document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = "image/*,.svg,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar"
    input.onchange = { _ ->
        val file = input.files?.get(0)
        if (file != null) {
            if (file.size.toInt() > MAX_EVENT_CHAT_FILE_BYTES) {
                kotlinx.browser.window.alert("File is too large. Maximum size is 50 MB.")
            } else {
                val reader = FileReader()
                reader.onload = {
                    val arrayBuffer = reader.result.unsafeCast<org.khronos.webgl.ArrayBuffer>()
                    val int8Array = org.khronos.webgl.Int8Array(buffer = arrayBuffer)
                    val bytes = int8Array.unsafeCast<ByteArray>()
                    onFilePicked(bytes, file.name, file.type)
                }
                reader.readAsArrayBuffer(file)
            }
        }
    }
    input.click()
}

/**
 * Binds the event chat panel to [viewModel] state + callbacks. Used in
 * three places (NEW/pending two-column layout, Pro completed right column,
 * non-Pro completed right column) so the wiring lives once.
 *
 * Returns nothing when the event has no chat (`chatId == null`) — the
 * caller can rely on a no-op render in that case.
 */
@Composable
private fun EventChatPanelBound(
    viewModel: EventDetailViewModel,
    state: EventDetailUiState,
    isLoggedIn: Boolean,
    onNavigateToLogin: () -> Unit,
    onViewProfile: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val event = state.event ?: return
    if (event.chatId == null) return
    EventChatPanel(
        messages = state.chatMessages,
        messageText = state.chatMessageText,
        isLoading = state.chatLoading,
        onTextChanged = { viewModel.updateChatMessageText(it) },
        onSend = { viewModel.sendChatMessage() },
        onTyping = { viewModel.sendChatTyping() },
        onLoadMore = { viewModel.loadMoreChatMessages() },
        hasMore = state.chatHasMore,
        participants = event.participants,
        sportName = event.sportName ?: "football",
        userParticipation = state.userParticipation,
        isPrivateEvent = event.privacy.uppercase() == "PRIVATE",
        onViewProfile = onViewProfile,
        onJoinClick = {
            if (isLoggedIn) viewModel.joinEvent() else onNavigateToLogin()
        },
        pinnedMessage = state.chatPinnedMessage,
        replyingToMessage = state.chatReplyingTo,
        editingMessage = state.chatEditing,
        pendingFile = state.chatPendingFile,
        uploadingFile = state.chatUploadingFile,
        onReaction = { messageId, emoji -> viewModel.toggleChatReaction(messageId, emoji) },
        onReply = { viewModel.startChatReply(it) },
        onCancelReply = { viewModel.cancelChatReply() },
        onEdit = { viewModel.startChatEdit(it) },
        onCancelEdit = { viewModel.cancelChatEdit() },
        onSubmitEdit = { viewModel.submitChatEdit() },
        onDelete = { viewModel.deleteChatMessage(it) },
        onPin = { viewModel.pinChatMessage(it) },
        onAttachmentClick = {
            pickEventChatFile { bytes, name, contentType ->
                viewModel.setChatPendingFile(bytes, name, contentType)
            }
        },
        onSendFile = { viewModel.sendChatMediaMessage() },
        onCancelFile = { viewModel.clearChatPendingFile() },
        modifier = modifier,
    )
}

/**
 * "Add Media" CTA pinned right above the media grid on both Pro and non-Pro
 * completed events. Sport-tinted pill (circle + label) — uses
 * [LocalSportColors] for the fill so the colour matches the active sport.
 */
@Composable
private fun AddMediaButtonRow(onClick: () -> Unit, showProPlus: Boolean = false) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.End,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = Modifier
                .cursor(Cursor.Pointer)
                .onClick { onClick() },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .sSize(44)
                    .sBorderRadius(100)
                    .backgroundColor(sportColors.primary),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    src = AppIcons.Add,
                    modifier = Modifier.sSize(16).styleModifier {
                        property("filter", sportColors.iconFilterOnPrimary)
                    },
                )
            }
            Box(
                modifier = Modifier
                    .sHeight(16)
                    .sWidth(7)
                    .backgroundColor(sportColors.primary)
                    .sMarginLeft(-4)
                    .zIndex(0),
            )
            Box(
                modifier = Modifier
                    .sHeight(44)
                    .sBorderRadius(22)
                    .backgroundColor(sportColors.primary)
                    .sPadding(10, 24)
                    .sMarginLeft(-4),
                contentAlignment = Alignment.Center,
            ) {
                Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        t.pageAddMedia,
                        modifier = Modifier
                            .bodyLMedium()
                            .color(sportColors.onPrimary)
                            .styleModifier { property("white-space", "nowrap") },
                    )
                    // Media uploads are a subscription feature — PRO+ pill
                    // while unsubscribed (tap opens the paywall via onClick).
                    if (showProPlus) {
                        com.arena_web.ui.components.overlay.ProPlusPill(onPrimary = true)
                    }
                }
            }
        }
    }
}
